/**
 * @description context 抽象
 * @author duanjun
 */

import React from 'react';
import Scope from './scope';
import { IController, IControllerAction, IMethodsProps } from './interface';
// const log = new Log('commentContext');

export default abstract class ContextProvider<S, K extends IController<S>, M = {}> extends React.PureComponent<
    Partial<S> & IMethodsProps<M>,
    S
> {
    protected boundController: K;
    protected controller: K;
    protected methods?: M;
    protected scope: Scope<S>;
    protected Provider: React.Context<S & K & M>['Provider'];
    constructor(
        props: Partial<S> & IMethodsProps<M>,
        bloc: {
            initModel: S;
            controllerFactorys: Array<(scope: Scope<S>) => Partial<K>>;
            Provider: React.Context<S & K & M>['Provider'];
        },
    ) {
        super(props);
        const { initModel, controllerFactorys, Provider } = bloc;
        const methods = props.methods;
        let childProps: Partial<S> & IMethodsProps<M> = {};
        if (methods) {
            this.methods = methods;
            Object.keys(props).forEach((key) => {
                type IKey = keyof S;
                const prop = key as IKey;
                if (prop !== 'methods') {
                    childProps[prop] = props[prop];
                }
            });
        } else {
            childProps = props;
        }

        this.scope = new Scope<S>(initModel);
        this.state = this.scope.model = Object.assign({}, this.scope.model, childProps);
        this.controller = this.componse(controllerFactorys)(this.scope);
        this.Provider = Provider;
        this.boundController = this.bindController();
    }

    public call<TS extends any[], R>(fn: (...args: TS[]) => R, ...args: TS[]): R {
        return fn(...args);
    }

    public update(next: S, prev: S) {
        if (next !== prev) {
            // this.scope.model = next;
            this.setState(next);
        }
    }

    public controllerCreator() {
        //
    }

    public bindController() {
        type Key = keyof K;
        const controller = this.controller;
        const scope = this.scope;
        Object.keys(controller).forEach((key: string) => {
            const method = controller[key as Key];
            const controllerAction: IControllerAction<S> = (...args: any[]) => {
                const prevModel = scope.model;

                const result = method.apply(controller, args);
                if (result instanceof Promise) {
                    controllerAction.status = 'pending';
                    result.then((newModel) => {
                        this.update(newModel, prevModel);
                        controllerAction.status = 'resolved';
                    });
                } else {
                    this.update(result, prevModel);
                }

                return result;
            };
            // @ts-ignore
            controller[key as Key] = controllerAction;
        });
        return controller;
    }

    /**
     * @description 声明周期结束之后需要同步到全局store上的方法
     */
    public mapStateToStore() {
        //
    }

    public componentWillUnmount() {
        this.mapStateToStore();
    }

    public render() {
        const value = Object.assign({}, this.state, this.boundController, this.methods);
        return <this.Provider value={value}>{this.props.children}</this.Provider>;
    }

    protected componse(controllerFactorys: Array<(scope: Scope<S>) => Partial<K>>) {
        const factory = (scope: Scope<S>) => {
            let controllerMap!: K;
            controllerFactorys.forEach((controllerFactory) => {
                controllerMap = Object.assign({}, controllerMap, controllerFactory(scope));
            });
            return controllerMap;
        };
        return factory;
    }

    protected createScope(model: S) {
        return new Scope<S>(model);
    }
}
